import React, { useState, useEffect } from 'react';
import { CustomTour, CustomTourFormData, ValidationError } from '../types';
import AdminNavbar from '../components/admin/AdminNavbar';
import CustomTourList from '../components/admin/CustomTourList';
import { 
  fetchAllCustomTours, 
  createCustomTour, 
  updateCustomTour, 
  deleteCustomTour 
} from '../services/customTourService';

const AdminCustomToursPage: React.FC = () => {
  const [formData, setFormData] = useState<CustomTourFormData>({
    country: '',
    months: [],
  });

  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<string | null>(null);
  
  // Update mode state
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedCustomTour, setSelectedCustomTour] = useState<CustomTour | null>(null);
  const [customTours, setCustomTours] = useState<CustomTour[]>([]);
  const [customToursLoading, setCustomToursLoading] = useState(false);

  // Month options
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Comprehensive country list
  const countryOptions = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
    'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
    'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon',
    'Canada', 'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
    'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
    'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia',
    'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti',
    'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
    'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia',
    'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia',
    'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco',
    'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand',
    'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Panama',
    'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
    'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain',
    'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand',
    'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
    'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen',
    'Zambia', 'Zimbabwe'
  ];

  // Fetch all custom tours on component mount
  useEffect(() => {
    fetchCustomTours();
  }, []);

  const fetchCustomTours = async () => {
    setCustomToursLoading(true);
    try {
      const customToursData = await fetchAllCustomTours();
      setCustomTours(customToursData);
    } catch (error) {
      console.error('Error fetching custom tours:', error);
      setSubmitStatus('Error fetching custom tours. Please refresh the page.');
    } finally {
      setCustomToursLoading(false);
    }
  };

  const handleUpdateCustomTour = (customTour: CustomTour) => {
    setSelectedCustomTour(customTour);
    setIsUpdateMode(true);
    
    // Populate form with custom tour data
    setFormData({
      country: customTour.country,
      months: customTour.months || [],
    });
    
    // Clear any existing errors
    setErrors([]);
    setSubmitStatus(null);
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelUpdate = () => {
    setIsUpdateMode(false);
    setSelectedCustomTour(null);
    setFormData({
      country: '',
      months: [],
    });
    setErrors([]);
    setSubmitStatus(null);
  };

  const handleDeleteCustomTour = async (customTourId: string) => {
    try {
      await deleteCustomTour(customTourId);
      setSubmitStatus('Custom tour entry deleted successfully!');
      await fetchCustomTours(); // Refresh the list
      
      // Clear status after 3 seconds
      setTimeout(() => setSubmitStatus(null), 3000);
    } catch (error) {
      console.error('Error deleting custom tour:', error);
      setSubmitStatus('Error deleting custom tour entry. Please try again.');
    }
  };

  const validateForm = (): ValidationError[] => {
    const newErrors: ValidationError[] = [];

    // Country validation
    if (!formData.country.trim()) {
      newErrors.push({ field: 'country', message: 'Country is required' });
    } else if (formData.country.trim().length < 2 || formData.country.trim().length > 100) {
      newErrors.push({ field: 'country', message: 'Country name must be between 2 and 100 characters' });
    }

    // Months validation
    if (!formData.months || formData.months.length === 0) {
      newErrors.push({ field: 'months', message: 'At least one month is required' });
    } else {
      // Validate each selected month
      for (const month of formData.months) {
        if (!monthOptions.includes(month)) {
          newErrors.push({ field: 'months', message: `Invalid month selected: ${month}` });
          break;
        }
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors([]);
    setSubmitStatus(null);

    try {
      if (isUpdateMode && selectedCustomTour) {
        // Update existing custom tour
        await updateCustomTour(selectedCustomTour.id, formData);
        setSubmitStatus('Custom tour entry updated successfully!');
      } else {
        // Create new custom tour
        await createCustomTour(formData);
        setSubmitStatus('Custom tour entry created successfully!');
      }

      // Reset form
      setFormData({
        country: '',
        months: [],
      });
      setIsUpdateMode(false);
      setSelectedCustomTour(null);
      
      // Refresh the list
      await fetchCustomTours();
      
      // Clear status after 3 seconds
      setTimeout(() => setSubmitStatus(null), 3000);
      
    } catch (error: any) {
      console.error('Error submitting custom tour:', error);
      setSubmitStatus(error.message || 'Error saving custom tour entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    setErrors(prev => prev.filter(error => error.field !== name));
  };

  const handleMonthToggle = (month: string) => {
    setFormData(prev => ({
      ...prev,
      months: prev.months.includes(month)
        ? prev.months.filter(m => m !== month)
        : [...prev.months, month]
    }));
    
    // Clear error for months when user selects/deselects
    setErrors(prev => prev.filter(error => error.field !== 'months'));
  };

  return (
    <div className="min-h-screen bg-[var(--secondary-pearl)]">
      <AdminNavbar />
      <div className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="heading-primary text-[var(--primary-deep-blue)] mb-2">
                Custom Tours Management
              </h1>
              <p className="body-regular text-[var(--secondary-slate)]">
                Manage custom tour entries with country and month information
              </p>
            </div>

            {/* Status Messages */}
            {submitStatus && (
              <div className={`mb-6 p-4 rounded-lg ${
                submitStatus.includes('successfully') 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {submitStatus}
              </div>
            )}

            {/* Form Section */}
            <div className="card mb-8">
              <div className="card-content">
                <div className="mb-6">
                  <h2 className="heading-secondary text-[var(--primary-deep-blue)] mb-2">
                    {isUpdateMode ? 'Update Custom Tour Entry' : 'Add New Custom Tour Entry'}
                  </h2>
                  <p className="body-regular text-[var(--secondary-slate)]">
                    {isUpdateMode 
                      ? 'Modify the country and month information for this entry.' 
                      : 'Create a new custom tour entry with country and month details.'
                    }
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-6">
                    {/* Country Field */}
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-[var(--primary-deep-blue)] mb-2">
                        Country *
                      </label>
                      <select
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[var(--primary-deep-blue)] focus:border-transparent transition-all duration-200 ${
                          errors.some(e => e.field === 'country') 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select a country</option>
                        {countryOptions.map(country => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                      {errors.some(e => e.field === 'country') && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.find(e => e.field === 'country')?.message}
                        </p>
                      )}
                    </div>

                    {/* Months Field - Tile Calendar */}
                    <div>
                      <label className="block text-sm font-medium text-[var(--primary-deep-blue)] mb-2">
                        Months * <span className="text-sm text-gray-500">(Click to select multiple months)</span>
                      </label>
                      
                      {/* Selected months summary */}
                      {formData.months.length > 0 && (
                        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <span className="font-medium">Selected:</span> {formData.months.join(', ')}
                          </p>
                        </div>
                      )}
                      
                      {/* Month tiles */}
                      <div className="grid grid-cols-12 gap-2">
                        {monthOptions.map(month => (
                          <button
                            key={month}
                            type="button"
                            onClick={() => handleMonthToggle(month)}
                            className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all duration-200 ${
                              formData.months.includes(month)
                                ? 'bg-solid-primary text-white border-[#1e3a8a]'
                                : 'bg-white text-[var(--primary-deep-blue)] border-gray-300 hover:bg-blue-50 hover:border-blue-300'
                            }`}
                          >
                            {month.substring(0, 3)}
                          </button>
                        ))}
                      </div>
                      
                      {errors.some(e => e.field === 'months') && (
                        <p className="mt-2 text-sm text-red-600">
                          {errors.find(e => e.field === 'months')?.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`btn btn-primary flex-1 sm:flex-none ${
                        isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSubmitting 
                        ? (isUpdateMode ? 'Updating...' : 'Creating...') 
                        : (isUpdateMode ? 'Update Entry' : 'Create Entry')
                      }
                    </button>
                    
                    {isUpdateMode && (
                      <button
                        type="button"
                        onClick={handleCancelUpdate}
                        className="btn btn-secondary flex-1 sm:flex-none"
                      >
                        Cancel Update
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Custom Tours List Section */}
            <div className="mt-12">
              <div className="mb-6">
                <h2 className="heading-secondary text-[var(--primary-deep-blue)] mb-2">
                  All Custom Tour Entries
                </h2>
                <p className="body-regular text-[var(--secondary-slate)]">
                  Click "Update Entry" to modify custom tour details or "Delete Entry" to remove entries permanently
                </p>
              </div>
              
              <CustomTourList 
                customTours={customTours} 
                onUpdateCustomTour={handleUpdateCustomTour}
                onDeleteCustomTour={handleDeleteCustomTour}
                loading={customToursLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomToursPage;
